import {BarChart} from '@/_metronic/partials/content/chart/BarChart'
import {memo, useEffect, useState} from 'react'
import {
  useQueryResponse,
  useQueryResponseData,
  useQueryResponseLoading,
} from './core/QueryResponseProvider'
import { RevenueSharingCard } from './components/RevenueSharingCard'
import { RevenueSharingCardSharing } from './components/RevenueSharingCardSharing'
import { RevenueServiceSharingHistory } from './components/RevenueServiceSharingHistory'
import { RevenueSharingWib } from './components/RevenueSharingWib'
import { RevenueSharingWita } from './components/RevenueSharingWita'
import { RevenueSharingWit } from './components/RevenueSharingWit'
import { AchievementProgress } from '../dashboard-forecast/components/AchievementProgress'
import { useQueryResponseMonthly, useQueryResponseMonthlyData, useQueryResponseMonthlyLoading } from '../dashboard-forecast/core/QueryResponseMonthlyProvider'
import { useQueryResponseNewOutletData } from './core/QueryResponseActualNewOutletProvider'
import { useQueryResponseActualTargetData } from './core/QueryResponseActualTargetProvider'
import DashboardExistingVsProjectionVsNewOutletCard from './components/DashboardExistingVsProjectionVsNewOutletCard'
import DashboardPnLvsTargetTargetCard from './components/DashboardPnLvsTargetCard'
import DashboardPnLLastYearVsTargetCurrentYearCard from './components/DashboardPnLLastYearVsTargetCurrentYearCard'
import DashboardRevenueSummaryAll from './components/DashboardRevenueSummaryAll'

const MemoizedRevenueSharingCard = memo(RevenueSharingCard)
const MemoizedRevenueSharingCardSharing = memo(RevenueSharingCardSharing)
const MemoizeRevenueSharingHistory = memo(RevenueServiceSharingHistory)
const MemoizeRevenueWib = memo(RevenueSharingWib)
const MemoizeRevenueWita = memo(RevenueSharingWita)
const MemoizeRevenueWit = memo(RevenueSharingWit)

const Overview = () => {
  const isLoading = useQueryResponseLoading()
  const response = useQueryResponse()
  const data = useQueryResponseData()
  const responseMontly = useQueryResponseMonthly()
  const isLoadingMonthly = useQueryResponseMonthlyLoading()
  const dataActualMonthly = useQueryResponseMonthlyData()
  const dataActualActualTarget = useQueryResponseActualTargetData()
  const dataActualNewOutlet = useQueryResponseNewOutletData()
  const [arrayOfLabelsActualTargets, setArrayOfLabelsActualTargets] = useState<string[]>([])
  const [arrayOfLabelsNewOutlets, setArrayOfLabelsNewOutlets] = useState<string[]>([])

  function numberWithCommas(x) {
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, '.')
  }

  function numberFormatWithCommas(x) {
    return Intl.NumberFormat('in-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 2,
    })
      .format(parseFloat(x || '0'))
      .replace('Rp', '')
      .trim()
  }

  useEffect(() => {
    if (dataActualActualTarget) {
      setArrayOfLabelsActualTargets([])
      if (dataActualActualTarget) {
        const arrayOfLabelsString = dataActualActualTarget?.map((obj, index) => {
          return obj.month_name ? obj.month_name : ''
        })
        setArrayOfLabelsActualTargets(arrayOfLabelsString)
      }
    }
  }, [dataActualActualTarget])
  
  useEffect(() => {
    if (dataActualNewOutlet) {
      setArrayOfLabelsNewOutlets([])
      if (dataActualNewOutlet) {
        const arrayOfLabelsString = dataActualNewOutlet?.map((obj, index) => {
          return obj.month_name ? obj.month_name : ''
        })
        setArrayOfLabelsNewOutlets(arrayOfLabelsString)
      }
    }
  }, [dataActualNewOutlet])

  return (
    <>
      <div className='row g-5 g-xl-8 mt-5'>
        <div className='col-xl-4 mb-5'>
          <AchievementProgress
            className='primary shadow-wit'
            chartColor='primary'
            chartHeight='250'
            title='Revenue Achievement'
            subTitle='Compared to your target'
            bar={
              dataActualMonthly &&
              dataActualMonthly.sales_achievement &&
              dataActualMonthly?.sales_achievement?.completion_rate &&
              dataActualMonthly?.sales_achievement?.completion_rate?.origin
            }
            actual={
              dataActualMonthly &&
              dataActualMonthly.sales_achievement &&
              dataActualMonthly?.sales_achievement?.actual_sales
                ? 'Rp. ' +
                  numberFormatWithCommas(dataActualMonthly?.sales_achievement?.actual_sales)
                : 'Rp. 0'
            }
            target={
              dataActualMonthly &&
              dataActualMonthly.sales_achievement &&
              dataActualMonthly?.sales_achievement?.sales_target
                ? 'Rp. ' +
                  numberFormatWithCommas(dataActualMonthly?.sales_achievement?.sales_target)
                : 'Rp. 0'
            }
            isLoading={
              isLoadingMonthly || (responseMontly.response?.response as any) === 'no result data'
            }
          />
        </div>

        <div className='col-xl-4 mb-5'>
          <AchievementProgress
            className='primary shadow-wit'
            chartColor='primary'
            chartHeight='250'
            title='Revenue Achievement Alaya'
            subTitle='Compared to your target'
            bar={
              dataActualMonthly &&
              dataActualMonthly.sales_alaya_achievement &&
              dataActualMonthly?.sales_alaya_achievement?.completion_rate &&
              dataActualMonthly?.sales_alaya_achievement?.completion_rate?.origin
            }
            actual={
              dataActualMonthly &&
              dataActualMonthly.sales_alaya_achievement &&
              dataActualMonthly?.sales_alaya_achievement?.actual_sales
                ? 'Rp. ' +
                  numberFormatWithCommas(dataActualMonthly?.sales_alaya_achievement?.actual_sales)
                : 'Rp. 0'
            }
            target={
              dataActualMonthly &&
              dataActualMonthly.sales_alaya_achievement &&
              dataActualMonthly?.sales_alaya_achievement?.sales_target
                ? 'Rp. ' +
                  numberFormatWithCommas(dataActualMonthly?.sales_alaya_achievement?.sales_target)
                : 'Rp. 0'
            }
            isLoading={
              isLoadingMonthly || (responseMontly.response?.response as any) === 'no result data'
            }
          />
        </div>

        <div className='col-xl-4'>
          <AchievementProgress
            className='success shadow-wit'
            chartColor='success'
            chartHeight='250'
            title='Treatments Achievement'
            subTitle='Compared to your target'
            bar={
              dataActualMonthly &&
              dataActualMonthly.treatment_achievement &&
              dataActualMonthly?.treatment_achievement?.completion_rate &&
              dataActualMonthly?.treatment_achievement?.completion_rate?.origin
            }
            actual={
              dataActualMonthly &&
              dataActualMonthly.treatment_achievement &&
              dataActualMonthly?.treatment_achievement?.actual_treatment
                ? numberWithCommas(dataActualMonthly?.treatment_achievement?.actual_treatment)
                : '0'
            }
            target={
              dataActualMonthly &&
              dataActualMonthly.treatment_achievement &&
              dataActualMonthly?.treatment_achievement?.treatment_target
                ? numberFormatWithCommas(dataActualMonthly?.treatment_achievement?.treatment_target)
                : '0'
            }
            isLoading={
              isLoadingMonthly || (responseMontly.response?.response as any) === 'no result data'
            }
          />
        </div>
      </div>

      <div className='mt-8'>
        <h4>Summary Information</h4>
        <div className='row'>
          <MemoizedRevenueSharingCard />
        </div>
        <div className='d-flex row mt-5'>
          <MemoizedRevenueSharingCardSharing />
        </div>
      </div>

      <div className='col-xl-12 g-5 g-xl-8 pt-10'>
        <MemoizeRevenueSharingHistory />
        <MemoizeRevenueWib />
        <MemoizeRevenueWita />
        <MemoizeRevenueWit />
      </div>

      <div className='col-xl-12 mt-10'>
        <BarChart
          className='shadow-wit'
          color='primary'
          title='Actual vs Target (New Outlet)'
          subtitle=''
          height='260px'
          series={[
            {
              name: 'Actual',
              data: dataActualActualTarget?.map((obj) =>
                obj.actual_revenue_alaya ? obj.actual_revenue_alaya : 0
              ),
            },
            {
              name: 'Target',
              data: dataActualActualTarget?.map((obj) =>
                obj.target_revenue_alaya ? obj.target_revenue_alaya : 0
              ),
            },
          ]}
          categories={arrayOfLabelsNewOutlets}
          total={0}
          isTotal={false}
          isCurrency= {true}
          data={dataActualActualTarget?.map((x) => ({
            month: `${x.month_name} ${x.years}`,
            actual_outlet: x.actual_outlet,
            actual_revenue_all: x.actual_revenue_all,
            actual_revenue_alaya: x.actual_revenue_alaya,
            target_outlet: x.target_outlet,
            target_revenue_all: x.target_revenue_all,
            target_revenue_alaya: x.target_revenue_alaya,
          }))}
          headers={['Bulan', 'Actual Outlet', 'Actual Revenue All', 'Actual Revenue Alaya', 'Target Outlet', 'Target Revenue All', 'Target Revenue Alaya']}
          formatCurrency={['actual_revenue_all', 'actual_revenue_alaya', 'target_revenue_all', 'target_revenue_alaya']}
          isLoading={isLoading || (response.response?.response as any) === 'no result data'}
        />
      </div>

      <div className='col-xl-12 mt-10'>
        <BarChart
          className='shadow-wit'
          color='primary'
          title='Actual Outlet vs New Outlet'
          subtitle=''
          height='260px'
          series={[
            {
              name: 'Actual',
              data: dataActualNewOutlet?.map((obj) =>
                obj.revenue_actual_outlet_alaya ? obj.revenue_actual_outlet_alaya : 0
              ),
            },
            {
              name: 'New Outlet',
              data: dataActualNewOutlet?.map((obj) =>
                obj.revenue_new_outlet_alaya ? obj.revenue_new_outlet_alaya : 0
              ),
            },
          ]}
          categories={arrayOfLabelsNewOutlets}
          total={0}
          isTotal={false}
          isCurrency= {true}
          data={dataActualNewOutlet?.map((x) => ({
            month: `${x.month_name} ${x.year}`,
            total_actual_outlet: x.total_actual_outlet,
            revenue_actual_outlet_all: x.revenue_actual_outlet_all,
            revenue_actual_outlet_alaya: x.revenue_actual_outlet_alaya,
            total_new_outlet: x.total_new_outlet,
            revenue_new_outlet_all: x.revenue_new_outlet_all,
            revenue_new_outlet_alaya: x.revenue_new_outlet_alaya,
          }))}
          headers={['Bulan', 'Total Actual Outlet', 'Revenue Actual_Outlet All', 'Revenue Actual Outlet Alaya', 'Total New Outlet', 'Revenue New Outlet All', 'Revenue New Outlet Alaya']}
          formatCurrency={['revenue_actual_outlet_all', 'revenue_actual_outlet_alaya', 'revenue_new_outlet_all', 'revenue_new_outlet_alaya']}
          isLoading={isLoading || (response.response?.response as any) === 'no result data'}
        />
      </div>
      <div className='col-xl-12 mt-10'>
        <DashboardExistingVsProjectionVsNewOutletCard />
      </div>
      <div className='col-xl-12 mt-10'>
        <DashboardPnLLastYearVsTargetCurrentYearCard />
      </div>
      <div className='col-xl-12 mt-10'>
        <DashboardRevenueSummaryAll />
      </div>
    </>
  )
}

export default Overview
